import React, { useState } from 'react';
import { Button } from './ui/button';
import { X, Upload, Image as ImageIcon, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUploader = ({ images = [], onChange }) => {
  const [uploading, setUploading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validação de extensões e tamanho
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    for (const file of files) {
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!allowedExtensions.includes(fileExtension)) {
        alert(`Arquivo "${file.name}" não permitido. Use: ${allowedExtensions.join(', ')}`);
        e.target.value = '';
        return;
      }

      if (file.size > maxSize) {
        alert(`Arquivo "${file.name}" muito grande. Tamanho máximo: 5MB`);
        e.target.value = '';
        return;
      }
    }

    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${BACKEND_URL}/api/admin/upload-image`,
          formData,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        return response.data.filename;
      });

      const uploadedFilenames = await Promise.all(uploadPromises);
      onChange([...images, ...uploadedFilenames]);
      alert(`${uploadedFilenames.length} imagem(ns) enviada(s) com sucesso!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao fazer upload';
      alert(errorMsg);
      console.error('Erro no upload:', error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async (filename, index) => {
    if (!window.confirm('Deseja remover esta imagem?')) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/delete-image/${filename}`,
        getAuthHeaders()
      );

      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);
    } catch (error) {
      // Image might already be deleted, just remove from list
      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);
    }
  };

  const moveImage = (index, direction) => {
    const newImages = [...images];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= images.length) return;
    
    // Swap images
    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
    onChange(newImages);
  };

  const setAsPrimary = (index) => {
    if (index === 0) return;
    const newImages = [...images];
    const primaryImage = newImages.splice(index, 1)[0];
    newImages.unshift(primaryImage);
    onChange(newImages);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-700">
          Imagens do Projeto *
        </label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => document.getElementById('file-input').click()}
          className="flex items-center space-x-2"
        >
          <Upload size={16} />
          <span>{uploading ? 'Enviando...' : 'Adicionar Imagens'}</span>
        </Button>
        <input
          id="file-input"
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      <p className="text-xs text-gray-500">
        <strong>Formatos aceitos:</strong> JPG, JPEG, PNG, GIF, WEBP, SVG (máx. 5MB por arquivo)
      </p>

      {images.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">Nenhuma imagem adicionada</p>
          <p className="text-sm text-gray-500">
            Clique no botão "Adicionar Imagens" para fazer upload
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map((filename, index) => (
            <div key={index} className="relative group">
              <img
                src={getImageUrl(filename)}
                alt={`Imagem ${index + 1}`}
                className={`w-full h-32 object-cover rounded-lg border-2 transition-all ${
                  index === 0 ? 'border-[#00A86B] ring-2 ring-[#00A86B] ring-opacity-50' : 'border-gray-200'
                }`}
              />
              
              {/* Overlay with controls */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all rounded-lg flex items-center justify-center gap-2">
                {/* Move left */}
                {index > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => moveImage(index, 'left')}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                )}
                
                {/* Set as primary */}
                {index !== 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => setAsPrimary(index)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 bg-[#00A86B] hover:bg-[#008c5a] text-white"
                    title="Definir como principal"
                  >
                    <Star size={16} />
                  </Button>
                )}
                
                {/* Delete */}
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={() => handleRemove(filename, index)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                >
                  <X size={16} />
                </Button>
                
                {/* Move right */}
                {index < images.length - 1 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => moveImage(index, 'right')}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                  >
                    <ChevronRight size={16} />
                  </Button>
                )}
              </div>
              
              {/* Primary badge */}
              {index === 0 && (
                <div className="absolute top-2 left-2 bg-[#00A86B] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center space-x-1">
                  <Star size={12} fill="white" />
                  <span>Principal</span>
                </div>
              )}
              
              {/* Image number */}
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <p className="text-xs text-gray-500">
          {images.length} imagem(ns) adicionada(s). A primeira imagem será a principal.
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
